import { createSelector } from 'reselect';
import {
  parseTravelDetails,
  getTravelClass,
  searchStringToObject,
  searchParamObjectToUrl,
  getDurationForDisplay,
  checkDocumentTypeIfExists,
  getPassengerType,
  checkDocumentTypeIfExistsNew
} from '../utils/reviewPageUtils';
import { getRepriceReqDetails } from './thunks';
import { experimentMetaDataSelector } from '../../Home/redux/selectors';
import { IFlightDetailsCardSelector, IFlightDetailsSelector } from './types';
import { selectedPromoCodeSelector } from '../../SRP/redux-lite/selectors';
import { getFareKey } from '../../utils/common/utils';
import { TRAVELLER_KEY_NAME as TravellerKey } from '@stencil/flights-constants/src/default-values';

export const brpStateSelector = state => state.brp;
export const homeStateSelector = state => state.home;
export const srpSelector = state => state.srp;

export const tokenSelector = createSelector(brpStateSelector, brp => brp.token);

export const txnIdSelector = createSelector(brpStateSelector, brp => brp.txnId);

export const flavourSelector = createSelector(homeStateSelector, home => home.flavour);

export const urlSearchParamSelector = createSelector(brpStateSelector, brp => brp.urlParams);

export const repriceParamsSelector = createSelector(
  [brpStateSelector, flavourSelector],
  (brp, flavour) => {
    return {
      ...brp.urlParams,
      flavour
    };
  }
);

export const repriceDataSelector = createSelector(brpStateSelector, brp => brp.repriceData);
export const insuranceVersionSelector = createSelector(
  brpStateSelector,
  brp => brp.repriceData?.ipd?.v || null
);

export const insurancecChargesSelector = createSelector(
  brpStateSelector,
  brp => brp.repriceData?.ipd?.charges || null
);

export const leadsDataSelector = createSelector(
  brpStateSelector,
  brp => brp?.repriceData?.l || null
);

export const getPriceDataSelector = createSelector(brpStateSelector, brp => brp.getPriceData);

export const getPriceTimerIdSelector = createSelector(brpStateSelector, brp => brp.getPriceTimerId);

export const bookingReviewDataSelector = createSelector(
  brpStateSelector,
  brp => brp.bookingReviewData
);

export const minifiedDataSelector = createSelector(brpStateSelector, brp => brp.minifiedData);

export const baggageDataSelector = createSelector(brpStateSelector, brp => brp.baggageData);

export const miniRulesDataSelector = createSelector(brpStateSelector, brp => brp.miniRulesData);

export const metaDataSelector = createSelector(
  brpStateSelector,
  brp => brp?.commonFareRulesData?.meta || null
);

export const fareRulesDataSelector = createSelector(
  brpStateSelector,
  brp => brp?.commonFareRulesData?.fare_rules || null
);

export const loadingPromoCodeSelector = createSelector(
  brpStateSelector,
  brp => brp.loadingPromoCode
);

// export const showPromoModalSelector = createSelector(brpStateSelector, brp => brp.showPromoModal);

export const insuranceStateSelector = createSelector(brpStateSelector, brp => brp.insuranceChanged);

export const savedTravellersSelector = createSelector(repriceDataSelector, repriceData => {
  const travellers = repriceData?.f_rules?.associatedTravellers?.data || [];
  const travellerDocumentList =
    repriceData?.f_rules?.travellerFormDataSource?.TRAVELER_DOCUMENT_LIST?.list || [];
  const filterTravellers = travellers.reduce((travellers, traveller) => {
    const travellerType = getPassengerType(traveller?.pax_type);
    if (travellerType) {
      travellers.push({ ...traveller, pax_type: travellerType });
    }
    return travellers;
  }, []);
  return checkDocumentTypeIfExists(filterTravellers, travellerDocumentList);
});

export const savedTravellersNewSelector = createSelector(repriceDataSelector, repriceData => {
  const savedTravellers = repriceData?.f_rules?.savedTravellers || [];
  const travellerDocumentList =
    repriceData?.f_rules?.travellerFormDataSource?.TRAVELER_DOCUMENT_LIST?.list || [];
  return checkDocumentTypeIfExistsNew(savedTravellers, travellerDocumentList);
});

export const newTravellersSelector = createSelector(brpStateSelector, brp => brp.newTravellers);

export const selectedTravellersSelector = createSelector(
  brpStateSelector,
  brp => brp.selectedTravellers
);

export const currentTravellersSelector = createSelector(
  brpStateSelector,
  brp => brp.currentTravellers
);

export const gstDetailsSelector = createSelector(brpStateSelector, brp => brp.GSTDetails);

export const gstEnabledSelector = createSelector(brpStateSelector, brp => brp.GSTEnabled);

export const contactDetailsSelector = createSelector(brpStateSelector, brp => brp.contactDetails);

export const selectedBaggageSelector = createSelector(brpStateSelector, brp => brp.selectedBaggage);

export const selectedMealsSelector = createSelector(brpStateSelector, brp => brp.selectedMeals);

export const selectedSeatsSelector = createSelector(brpStateSelector, brp => brp.selectedSeats);

export const mealsAmtSelector = createSelector(brpStateSelector, brp => brp.mealsAmount);

export const baggageAmtSelector = createSelector(brpStateSelector, brp => brp.baggageAmount);

export const seatsAmtSelector = createSelector(brpStateSelector, brp => brp.seatsAmount);

export const convFeeSelector = createSelector(repriceDataSelector, repriseData => repriseData.cf);

export const submitResponseSelector = createSelector(brpStateSelector, brp => brp.submitResponse);

export const genericErrorSelector = createSelector(brpStateSelector, brp => brp.genericError);

export const reviewBundlesSelector = createSelector(brpStateSelector, brp => brp.reviewBundles);

export const reviewWidgetOrderSelector = createSelector(
  brpStateSelector,
  brp => brp.reviewWidgetOrder
);

export const webReviewWidgetOrderSelector = createSelector(
  brpStateSelector,
  brp => brp.webReviewWidgetOrder
);

export const redirectErrorSelector = createSelector(brpStateSelector, brp => {
  return {
    redirectError: brp.redirectToSrpErrors,
    redirectErrorHeading: brp.redirectToSrpErrorHeading
  };
});

export const isMulticiySelector = createSelector(
  brpStateSelector,
  brp => brp.isMulticity || brp?.brpData?.query?.isMulticity
);

export const showLoginModalSelector = createSelector(brpStateSelector, brp => brp.showLoginModal);

export const submitLoadingSelector = createSelector(brpStateSelector, brp => brp.submitLoading);

export const skippedToPaymentsSelector = createSelector(
  brpStateSelector,
  brp => brp.skippedToPayments
);

export const insuranceDisplayedSelector = createSelector(
  brpStateSelector,
  brp => brp.insuranceDisplayed
);

export const cabDisplayedSelector = createSelector(brpStateSelector, brp => brp.cabDisplayed);

export const donationDisplayedSelector = createSelector(
  brpStateSelector,
  brp => brp.donationDisplayed
);

export const addonRetriedSelector = createSelector(brpStateSelector, brp => brp.addonRetried);

// export const submitFareBreakupSelector = createSelector(
//   [submitResponseSelector, repriceDataSelector, getPriceDataSelector],
//   (submitResponse, repriceData, getPriceData) => {
//     if (!submitResponse) {
//       return null;
//     }
//     const submitPriceBreakup = submitResponse?.fs || submitResponse?.f?.fb || [];
//     let submitFareBreakup = [];
//     //let submitFareLockBreakup = [];

//     if (submitPriceBreakup && getPriceData && repriceData) {
//       const { repriced_fare, fare_diff_absorbed, fare_diff } = getPriceData;
//       const { frl } = repriceData;
//       const priceLockInd = submitPriceBreakup?.fbs?.findIndex(fb => fb.key === 'FARE_LOCK');
//       if (priceLockInd >= 0) {
//         submitPriceBreakup.fbs[priceLockInd]?.fares.forEach(fare => {
//           if (fare_diff_absorbed > 0) {
//             fare.ftt = {
//               fda: fare_diff_absorbed,
//               la: frl, //locked at
//               ft: repriced_fare, //fare today
//               isEqOrAboveThreshold: fare_diff > 0
//             };
//             //submitFareLockBreakup.push(fare);
//           }
//         });
//       }
//     }
//     return {
//       submitFareBreakup,
//       submitPriceBreakup
//       //submitFareLockBreakup
//     };
//   }
// );

export const submitTotalAmtSelector = createSelector(submitResponseSelector, submitResponse => {
  if (!submitResponse) {
    return null;
  }
  return submitResponse?.final_amount;
});

export const ssdSelector = createSelector(
  submitResponseSelector,
  submitResponse => submitResponse.ssd
);

export const selectedFlightSelector = createSelector(srpSelector, srp => srp && srp.selectedFlight);

export const onwardFlightFareFamilyIndexSelector = createSelector(
  srpSelector,
  srp => srp && srp.onwardFlightFareFamilyIndex
);

export const returnFlightFareFamilyIndexSelector = createSelector(
  srpSelector,
  srp => srp && srp.returnFlightFareFamilyIndex
);

export const gstFormDetailsSelector = createSelector(
  repriceDataSelector,
  repriceData =>
    repriceData &&
    repriceData.f_rules &&
    repriceData.f_rules.travellerFormDetail &&
    repriceData.f_rules.travellerFormDetail.gstFormDetails
);

export const travellerFormRulesSelector = createSelector(
  repriceDataSelector,
  repriceData =>
    repriceData &&
    repriceData.f_rules &&
    repriceData.f_rules.travellerFormDetail &&
    repriceData.f_rules.travellerFormDetail.paxWiseTravellerFormDetails
);

export const noLastNameRuleDetailsSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.f_rules && repriceData.f_rules.ruleDetails
);

export const prefillTravellersSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.rtl
);

export const prefillEmailMobileSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.emp
);

export const nationalityListSelector = createSelector(
  repriceDataSelector,
  repriceData =>
    (repriceData &&
      repriceData.f_rules &&
      repriceData.f_rules.travellerFormDataSource &&
      repriceData.f_rules.travellerFormDataSource.NATIONALITY &&
      repriceData.f_rules.travellerFormDataSource.NATIONALITY.list) ||
    []
);

export const passportCountyListSelector = createSelector(
  repriceDataSelector,
  repriceData =>
    (repriceData &&
      repriceData.f_rules &&
      repriceData.f_rules.travellerFormDataSource &&
      repriceData.f_rules.travellerFormDataSource.PASSPORT_ISSUING_COUNTRY &&
      repriceData.f_rules.travellerFormDataSource.PASSPORT_ISSUING_COUNTRY.list) ||
    (repriceData &&
      repriceData.f_rules &&
      repriceData.f_rules.travellerFormDataSource &&
      repriceData.f_rules.travellerFormDataSource.NATIONALITY &&
      repriceData.f_rules.travellerFormDataSource.NATIONALITY.list)
);

export const countryListSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.f_rules?.travellerFormDataSource?.COUNTRY_NAME?.list
);

export const travellerFormDataSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.f_rules?.travellerFormDataSource
);
// export const frequentFlierAirlinesSelector = createSelector(
//   repriceDataSelector,
//   repriceData => repriceData?.f_rules?.travellerFormDataSource?.FREQUENT_FLIER_AIRLINE_AI?.list
// );

export const dateOfTravelSelector = createSelector(repriceDataSelector, repriceData => {
  if (repriceData && repriceData.fd) {
    const date = repriceData.fd.split('-');
    return new Date(date[0], parseInt(date[1], 10) - 1, date[2]);
  } else {
    return null;
  }
});

export const isDomesticSelector = createSelector(
  [srpSelector, repriceDataSelector],
  (srp, repriceData) => {
    const isInternational =
      (srp &&
        srp.queryParam &&
        srp.queryParam.split('-')[8] &&
        srp.queryParam.split('-')[8].includes('I')) ||
      (repriceData && repriceData.int);
    return !isInternational;
  }
);

export const querySelector = createSelector(
  [bookingReviewDataSelector, isMulticiySelector, srpSelector],
  (bookingReviewData, isMulticity, srp) => {
    if (isMulticity) {
      return (
        bookingReviewData && bookingReviewData.queryParam && `air-${bookingReviewData.queryParam}`
      );
    }
    return (
      (bookingReviewData && bookingReviewData.queryParam && bookingReviewData.queryParam) ||
      (srp && srp.queryParam && 'air-' + srp.queryParam)
    );
  }
);

export const queryArraySelector = createSelector(querySelector, query => {
  const queryArray = query && query.split('-');
  return queryArray;
});

export const paxTypeCountSelector = createSelector(queryArraySelector, query => {
  if (query) {
    return {
      adult: parseInt(query[5], 10),
      child: parseInt(query[6], 10),
      infant: parseInt(query[7], 10)
    };
  } else {
    return { adult: 1, child: 0, infant: 0 }; // Default
  }
});

export const paxTypeCountSelectorNew = createSelector(queryArraySelector, query => {
  if (query) {
    return {
      ADULT: parseInt(query[5], 10),
      CHILD: parseInt(query[6], 10),
      INFANT: parseInt(query[7], 10)
    };
  } else {
    return { ADULT: 1, CHILD: 0, INFANT: 0 }; // Default
  }
});

export const totalPaxSelector = createSelector(paxTypeCountSelector, pax => {
  let total = 0;

  Object.keys(pax).forEach(k => {
    total = total + pax[k];
  });

  return total;
});
// export const isRepriceDataAvailable = createSelector(brpStateSelector, brp => brp.repriceData && Object.keys(brp.repriceData).length > 0);

export const repriceLoadingSelector = createSelector(brpStateSelector, brp => brp.repriceLoading);

export const updatingFareFamilySelector = createSelector(
  brpStateSelector,
  brp => brp.updatingFareFamily
);

export const activeAddonsSelector = createSelector(brpStateSelector, brp => brp.activeAddons);

export const getPriceLoadingSelector = createSelector(brpStateSelector, brp => brp.getPriceLoading);

export const directlyLandedSelector = createSelector(srpSelector, srp => srp && srp.directlyLanded);

export const validateFormLoadingSelector = createSelector(
  brpStateSelector,
  brp => brp.validateFormLoading
);
// Request Selectors

export const repriceRequestSelector = createSelector(
  [
    bookingReviewDataSelector,
    urlSearchParamSelector,
    selectedFlightSelector,
    directlyLandedSelector,
    flavourSelector,
    experimentMetaDataSelector,
    onwardFlightFareFamilyIndexSelector,
    returnFlightFareFamilyIndexSelector
  ],
  (
    bookingReviewData,
    urlSearchParam,
    selectedFlight, //used for one way
    directlyLanded,
    flavour,
    experimentMetaData,
    onwardFlightFareFamilyIndex, //used for domestic round trip
    returnFlightFareFamilyIndex //used for domestic round trip
  ) => {
    const { rid = '', o = [], r = [] } = bookingReviewData.bookData || {};
    const srpSearchParams = searchStringToObject(
      (directlyLanded && directlyLanded.searchString) || location.search
    );

    const rcoms = {};
    if (o && o.length) {
      const onwFlt = o[0];
      rcoms[onwFlt.cid] = getFareKey(onwFlt, selectedFlight, onwardFlightFareFamilyIndex);
    }
    if (r && r.length) {
      const retFlt = r[0];
      rcoms[retFlt.cid] = getFareKey(retFlt, selectedFlight, returnFlightFareFamilyIndex);
    }
    if (!Object.keys(rcoms).length && !srpSearchParams?.rcoms) {
      return null;
    }
    return {
      ...urlSearchParam,
      query_data:
        bookingReviewData &&
        bookingReviewData.queryParam &&
        bookingReviewData.queryParam.split('-').slice(0, 9).join('-'),
      flavour,
      v: 1.6,
      rid,
      rcoms: JSON.stringify(rcoms),
      rkeys: JSON.stringify(
        Object.values(urlSearchParam.rcoms ? JSON.parse(urlSearchParam.rcoms) : rcoms)
      ),
      ...(onwardFlightFareFamilyIndex !== null && {
        onward_bundle_id: onwardFlightFareFamilyIndex
      }),
      ...(returnFlightFareFamilyIndex !== null && {
        return_bundle_id: returnFlightFareFamilyIndex
      }),
      ...(srpSearchParams && Object.keys(srpSearchParams).length ? srpSearchParams : null)
    };
  }
);

export const rulesRequestSelector = createSelector(
  [tokenSelector, bookingReviewDataSelector, repriceDataSelector],
  (token, bookingReviewData, repriceData) => {
    const cinfo =
      (bookingReviewData &&
        bookingReviewData.queryParam &&
        bookingReviewData.queryParam.split('-')) ||
      (repriceData && repriceData.query_data && repriceData.query_data.split('-'));

    if (!cinfo) {
      return null;
    }
    cinfo.splice(9);
    const request = {
      token,
      qry_data: '#' + cinfo.join('-')
    };
    return request;
  }
);

export const applyAddonsRequestSelector = createSelector(brpStateSelector, brp => ({
  meals_baggage: {
    meal: brp.selectedMeals,
    baggage: brp.selectedBaggage
  },
  seat:
    brp.selectedSeats && brp.selectedSeats.seat_data && brp.selectedSeats.seat_data.length > 0
      ? brp.selectedSeats
      : {}
}));

export const shouldSendGstInDetailsSelector = createSelector(
  brpStateSelector,
  brp => brp.repriceData.should_show_gst_in
);

export const gstInDetailsSelector = createSelector(brpStateSelector, brp => brp.gstInDetails);

export const submitRequestSelector = createSelector(
  [
    contactDetailsSelector,
    urlSearchParamSelector,
    tokenSelector,
    selectedTravellersSelector,
    gstEnabledSelector,
    gstDetailsSelector,
    flavourSelector,
    shouldSendGstInDetailsSelector,
    gstInDetailsSelector
  ],
  (
    contactDetails,
    urlSearchParam,
    token,
    selectedTravellers,
    gstEnabled,
    gstDetails,
    flavour,
    shouldSendGStInDetails,
    gstInDetails
  ) => {
    const travellers = {};

    Object.keys(selectedTravellers).forEach(type => {
      selectedTravellers[type].forEach((pax, i) => {
        travellers[type + 'gender' + (i + 1)] = pax?.gender || '';
        travellers[type + 'firstname' + (i + 1)] = pax?.givenname?.trim() || '';
        travellers[type + 'lastname' + (i + 1)] = pax?.lastname?.trim() || '';
        travellers[type + 'middlename' + (i + 1)] = '';

        if (pax.id) {
          travellers[type + 'id' + (i + 1)] = pax.id;
        }

        if (pax.dob) {
          travellers[type + 'dob' + (i + 1)] = pax.dob.trim();
        }
        if (pax.passport_num) {
          travellers[type + 'passport' + (i + 1)] = pax.passport_num.trim();
        }
        if (pax.nationality) {
          travellers[type + 'national' + (i + 1)] = pax.nationality.trim();
        }
        if (pax.passport_issuing_country) {
          travellers[type + 'passportissuingct' + (i + 1)] = pax.passport_issuing_country.trim();
        }
        if (pax.passport_expiry) {
          travellers[type + 'dateofexpiry' + (i + 1)] = pax.passport_expiry.trim();
        }
        if (pax.frequent_flier_no) {
          travellers[type + 'frequentno' + (i + 1)] = pax.frequent_flier_no.trim();
        }
        if (pax.frequent_flier_airline) {
          travellers[type + 'frequentairline' + (i + 1)] = pax.frequent_flier_airline.trim();
        }
        if (pax.document_id) {
          travellers[type + 'DocId' + (i + 1)] = pax.document_id.trim();
        }
        if (pax.document_type) {
          travellers[type + 'DocType' + (i + 1)] = pax.document_type.trim();
        }

        if (i === 0 && type === 'adult') {
          travellers['firstname'] = pax?.givenname?.trim() || '';
          travellers['lastname'] = pax?.lastname?.trim() || '';
        }
      });
    });

    const searchQuery = urlSearchParam || searchStringToObject(location?.search || '');
    delete searchQuery?.t;
    const query = searchParamObjectToUrl(searchQuery);

    const request = {
      mobilecode: contactDetails.mobilecode?.trim() || '91',
      mobile: contactDetails.mobile.trim(),
      email: contactDetails.email.trim(),
      token,
      flavour,
      ...travellers,
      pay_v2: true,
      enc: true,
      go_pn: 1,
      query: query !== '' ? query.replace('?', '&') : query
    };

    if (gstEnabled) {
      request['gstdata'] = JSON.stringify(gstDetails);
    }
    if (shouldSendGStInDetails && gstInDetails) {
      request['address'] = gstInDetails.address;
      request['pincode'] = gstInDetails.pincode;
      request['state_id'] = gstInDetails.stateId;
      request['state'] = gstInDetails.state;
    }
    return request;
  }
);

export const submitTravellerRequestSelector = createSelector(
  [
    contactDetailsSelector,
    selectedTravellersSelector,
    gstEnabledSelector,
    gstDetailsSelector,
    shouldSendGstInDetailsSelector,
    gstInDetailsSelector,
    countryListSelector
  ],
  (
    contactDetails,
    selectedTravellers,
    gstEnabled,
    gstDetails,
    shouldSendGStInDetails,
    gstInDetails,
    countryList
  ) => {
    const travellerDetails = [];
    const selectedTravallerIds = [];
    let travellerBillingAddressDetails = {};
    const gstFormDetails = {
      row_fields: {}
    };

    const contactFormDetails = {
      row_fields: {
        COUNTRY_CODE: contactDetails.mobilecode?.trim() || '91',
        COUNTRY_NAME: contactDetails.countryname,
        EMAIL: contactDetails.email.trim(),
        MOBILE_NUMBER: contactDetails.mobile.trim()
      }
    };

    if (countryList?.length) {
      const country = countryList.find(
        country => country.value === contactFormDetails.row_fields.COUNTRY_CODE
      );
      if (country) {
        contactFormDetails.row_fields.COUNTRY_NAME = country.key;
      }
    }

    Object.keys(selectedTravellers).forEach(type => {
      selectedTravellers[type].forEach((traveller, i) => {
        const { travellerId, infoDetail } = traveller;
        const { rowFields: pax } = infoDetail;
        const newTravaller = {
          traveller_id: travellerId,
          pax_type: type?.toUpperCase(),
          info_detail: {
            row_fields: {
              [TravellerKey.FIRST_NAME]: pax?.FIRST_NAME?.trim() || '',
              [TravellerKey.LAST_NAME]: pax?.LAST_NAME?.trim() || '',
              [TravellerKey.GENDER]: pax?.GENDER || '',
              ...(pax[TravellerKey.DOB] ? { [TravellerKey.DOB]: pax[TravellerKey.DOB] } : {}),
              ...(pax[TravellerKey.NATIONALITY]
                ? { [TravellerKey.NATIONALITY]: pax[TravellerKey.NATIONALITY].trim() }
                : {}),
              ...(pax[TravellerKey.PASSPORT_NUMBER]
                ? { [TravellerKey.PASSPORT_NUMBER]: pax[TravellerKey.PASSPORT_NUMBER].trim() }
                : {}),
              ...(pax[TravellerKey.PASSPORT_ISSUING_COUNTRY]
                ? {
                    [TravellerKey.PASSPORT_ISSUING_COUNTRY]:
                      pax[TravellerKey.PASSPORT_ISSUING_COUNTRY].trim()
                  }
                : {}),
              ...(pax[TravellerKey.PASSPORT_DATE_OF_EXPIRY]
                ? {
                    [TravellerKey.PASSPORT_DATE_OF_EXPIRY]:
                      pax[TravellerKey.PASSPORT_DATE_OF_EXPIRY].trim()
                  }
                : {})
            },
            grp_fields: {}
          }
        };

        if (pax[TravellerKey.DOCUMENT_ID] || pax[TravellerKey.DOCUMENT_TYPE]) {
          newTravaller.info_detail.grp_fields['TRAVELER_DOCUMENT'] = [];
          newTravaller.info_detail.grp_fields.TRAVELER_DOCUMENT.push({
            [TravellerKey.DOCUMENT_ID]: pax[TravellerKey.DOCUMENT_ID].trim(),
            [TravellerKey.DOCUMENT_TYPE]: pax[TravellerKey.DOCUMENT_TYPE].trim()
          });
        }

        if (pax[TravellerKey.FREQUENT_FLIER_NUM] || pax[TravellerKey.FREQUENT_FLIER_AIRLINE]) {
          newTravaller.info_detail.grp_fields['FREQUENT_FLIER'] = [];
          newTravaller.info_detail.grp_fields.FREQUENT_FLIER.push({
            [TravellerKey.FREQUENT_FLIER_NUM]: pax[TravellerKey.FREQUENT_FLIER_NUM].trim(),
            [TravellerKey.FREQUENT_FLIER_AIRLINE]: pax[TravellerKey.FREQUENT_FLIER_AIRLINE].trim()
          });
        }

        selectedTravallerIds.push(travellerId);
        travellerDetails.push(newTravaller);
      });
    });

    if (gstEnabled) {
      gstFormDetails.row_fields = {
        REGISTERED_COMPANY_NAME: gstDetails.cname.trim(),
        REGISTRATION_NO: gstDetails.gstn.trim()
      };
    }

    if (shouldSendGStInDetails && gstInDetails) {
      travellerBillingAddressDetails = {
        address: gstInDetails.address,
        postal_code: gstInDetails.pincode,
        state: gstInDetails.state,
        state_id: gstInDetails.stateId
      };
    }

    return {
      traveller_details: travellerDetails,
      selected_traveller_ids: selectedTravallerIds,
      contact_form_details: contactFormDetails,
      gst_form_details: gstFormDetails,
      traveller_billing_address_details: travellerBillingAddressDetails
    };
  }
);

export const covidInsuranceDataSelector = createSelector(repriceDataSelector, repriceData => {
  if ((repriceData.cie || repriceData.reviewStreaming) && repriceData.cid && repriceData.cid.ai) {
    return repriceData.cid;
  }
  return null;
});

export const hasAddonsSelector = createSelector(
  [repriceDataSelector, covidInsuranceDataSelector],
  (repriceData, covidInsuranceData) =>
    (repriceData.mb && repriceData.mb.length > 0) ||
    (repriceData.se && repriceData.sl) ||
    (repriceData.reviewStreaming && repriceData.sl) ||
    covidInsuranceData
);

// Page details
export const bookingReviewPageSelector = createSelector(
  [
    bookingReviewDataSelector,
    repriceDataSelector,
    getPriceDataSelector,
    getPriceTimerIdSelector,
    selectedTravellersSelector,
    totalPaxSelector,
    contactDetailsSelector,
    travellerFormRulesSelector,
    dateOfTravelSelector,
    hasAddonsSelector,
    submitResponseSelector,
    redirectErrorSelector
  ],
  (
    bookingReviewData,
    repriceData,
    getPriceData,
    getPriceTimerId,
    selectedTravellers,
    totalPax,
    contactDetails,
    travellerFormRules,
    dateOfTravel,
    hasAddons,
    submitResponse,
    redirectErrorObj
  ) => {
    const repriceDataAvailable = repriceData && Object.keys(repriceData).length > 0;
    const submitResponseAvailable = submitResponse && Object.keys(submitResponse).length > 0;

    if (redirectErrorObj.redirectError) {
      return {
        isLoading: false,
        isRoundTrip: repriceData.roundtrip || bookingReviewData.isRoundTrip || false
      };
    }

    if (submitResponseAvailable) {
      return { isLoading: false, isRoundTrip: repriceData.roundtrip || false };
    }

    if (!repriceDataAvailable) {
      return { isLoading: true, isRoundTrip: bookingReviewData.isRoundTrip || false };
    }

    const totalSelected = [
      ...selectedTravellers.adult,
      ...selectedTravellers.child,
      ...selectedTravellers.infant
    ].length;

    const bookingReviewDetails = {
      isLoading: false,
      isRoundTrip: repriceData.roundtrip,
      selectedTravellers,
      travellerFormRules,
      dateOfTravel,
      // isOnewayTrip: !repriceData.roundtrip,
      // fareDiff: getPriceData && getPriceData.farediff,
      // importantInfo: repriceData.ii,
      // getPriceDelay: repriceData.rf || 300000,
      // getPriceTimerId: getPriceTimerId,
      // containsOffer: repriceData.o && Array.isArray(repriceData.o) && repriceData.o.length > 0,
      hasAddons,
      allPaxSelected: totalPax === totalSelected,
      contactDetails
    };

    return bookingReviewDetails;
  }
);

export const isRoundTripSelector = createSelector(
  [bookingReviewDataSelector, repriceDataSelector],
  (bookingReviewData, repriceData) => {
    return repriceData.roundtrip || bookingReviewData.isRoundTrip || false;
  }
);

export const fareDiffSelector = createSelector(
  getPriceDataSelector,
  getPriceData => (getPriceData && getPriceData.farediff) || 0
);

export const fareDiffAbsorbedSelector = createSelector(
  getPriceDataSelector,
  getPriceData => getPriceData?.fare_diff_absorbed || 0
);

// export const importantInfoDataSelector = createSelector(
//   repriceDataSelector,
//   repriceData => repriceData?.ii || repriceData?.sii || null
// );

export const importantInfoDataSelector = createSelector(
  brpStateSelector,
  brpData => brpData?.importantInfo?.ii || brpData?.importantInfo?.sii || null
);

// export const importantInfoTemplateSelector = createSelector(repriceDataSelector, repriceData =>
//   Boolean(repriceData?.sii)
// );

export const importantInfoTemplateSelector = createSelector(brpStateSelector, brpData =>
  Boolean(brpData?.importantInfo?.sii)
);

export const delayedTicketingSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.delayed_ticketing || null
);
// export const importantInfoCheckboxSelector = createSelector(
//   repriceDataSelector,
//   repriceData => (repriceData && repriceData.i) || null
// );
export const importantInfoCheckboxSelector = createSelector(
  brpStateSelector,
  brpData => brpData?.importantInfo?.i || null
);

export const importantInfoWarningSelector = createSelector(
  brpStateSelector,
  brp => brp.importantInfoWarning
);

export const fareFamilyTypeAndFlightClassSelector = createSelector(
  [minifiedDataSelector],
  minifiedData => {
    if (
      !minifiedData ||
      typeof minifiedData.onward_bundle_id === 'undefined' ||
      !minifiedData.o[0].al
    ) {
      return null;
    }
    let farefamilyTypeAndFlightClass = {};
    const pnrGrp = minifiedData.o[0].al[minifiedData.onward_bundle_id].pg;
    const fareFamilyNameIndex = minifiedData.o[0]?.pf[pnrGrp]?.fn;
    farefamilyTypeAndFlightClass['fareFamilyName'] = minifiedData.i[fareFamilyNameIndex];
    farefamilyTypeAndFlightClass['flightClass'] = minifiedData.si?.ri?.cc;
    return farefamilyTypeAndFlightClass;
  }
);

export const charityDataSelector = createSelector([leadsDataSelector], leadsData => {
  if (leadsData) {
    for (let i = 0; i < leadsData.length; i++) {
      if (leadsData[i].t === 'charity') {
        return leadsData[i];
      }
    }
  }
  return null;
});

export const charityOptedSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.charity_opted
);

export const insuranceDataSelector = createSelector([leadsDataSelector], leadsData => {
  if (leadsData) {
    for (let i = 0; i < leadsData.length; i++) {
      if (leadsData[i].t === 'insurance') {
        return leadsData[i];
      }
    }
  }
  return null;
});

export const covidRefundSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.cwdd
);

// export const hasInsuraceActionDoneSelector = createSelector(
//   insuranceDataSelector,
//   insurance => insurance.d !== 2
// );

export const showInsurancePromptSelector = createSelector(
  brpStateSelector,
  brp => brp.showInsurancePrompt
);

export const showFareFamilyPromptSelector = createSelector(
  brpStateSelector,
  brp => brp.showFareFamilyPrompt
);

export const airportTransfersOptedSelector = createSelector(repriceDataSelector, repriceData => {
  const { fb } = repriceData;
  if (fb && fb.length) {
    for (let i = 0; i < fb.length; i++) {
      if (fb[i].k.toLowerCase().indexOf('cab') > -1 && fb[i].v > 0) {
        return true;
      }
    }
  }
  return false;
});

export const airportTransfersDataSelector = createSelector([leadsDataSelector], leadsData => {
  if (leadsData) {
    for (let i = 0; i < leadsData.length; i++) {
      if (
        leadsData[i].t === 'cab_assistance' &&
        leadsData[i].tx &&
        leadsData[i].tx.cab_list &&
        leadsData[i].tx.cab_list.length
      ) {
        return leadsData[i];
      }
    }
  }
  return null;
});

export const onwardFlightDetailsSelector = createSelector(
  [bookingReviewDataSelector, repriceDataSelector, minifiedDataSelector, miniRulesDataSelector],
  (bookingReviewData, repriceData, minifiedData, rules): IFlightDetailsSelector => {
    const bookData = bookingReviewData?.bookData || repriceData?.minified_data || minifiedData;
    if (bookData) {
      bookData.o = bookData.o instanceof Array ? bookData.o : [bookData.o];

      const lastFlightIndex = bookData.o[0].f.length - 1;
      const { i: srcIata = '', t: srcText = '' } = bookData?.a?.[bookData?.o?.[0]?.f?.[0]?.u] || {};
      const { i: desIata = '', t: desText = '' } =
        bookData?.a?.[bookData?.o?.[0]?.f?.[lastFlightIndex]?.g] || {};

      const refundability = rules?.terms?.[`${srcIata}-${desIata}`]?.wr || '';

      const travelClass =
        bookingReviewData?.queryParam?.split('-')[8] || repriceData?.query_data?.split('-')[8];

      // bookData.o[0].al && bookData.onward_bundle_id
      //   ? bookData.w[bookData.o[0].al[bookData.onward_bundle_id]?.w]
      //   : bookData.w[bookData.o[0]?.w];
      const flightDetails = {
        header: {
          src: srcText,
          dest: desText,
          refundability
        },
        footer: {
          travelClass,
          travelClassFull: getTravelClass(travelClass)
        },
        flightDuration: getDurationForDisplay(bookData.o[0].t),
        flights: bookData.o[0].f,
        ancillaryList: bookData.o[0].al || []
      };

      return flightDetails;
    } else {
      return { isLoading: true };
    }
  }
);

export const returnFlightDetailsSelector = createSelector(
  [
    bookingReviewDataSelector,
    repriceDataSelector,
    minifiedDataSelector,
    isRoundTripSelector,
    miniRulesDataSelector
  ],
  (bookingReviewData, repriceData, minifiedData, isRoundTrip, rules): IFlightDetailsSelector => {
    const bookData = bookingReviewData?.bookData || repriceData?.minified_data || minifiedData;

    if (bookData) {
      bookData.o = bookData.o instanceof Array ? bookData.o : [bookData.o];
      bookData.r = bookData.r instanceof Array ? bookData.r : [bookData.r];

      if (!isRoundTrip) {
        return null;
      }

      const returnFlightDetail = bookData.r[0] || bookData.o[0].r;

      const lastFlightIndex = returnFlightDetail.f && returnFlightDetail.f.length - 1;
      const { i: srcIata = '', t: srcText = '' } =
        bookData?.a?.[returnFlightDetail?.f?.[0]?.u] || {};
      const { i: desIata = '', t: desText = '' } =
        bookData?.a?.[returnFlightDetail?.f?.[lastFlightIndex]?.g] || {};
      const refundability = rules?.terms?.[`${srcIata}-${desIata}`]?.wr || '';

      const travelClass =
        bookingReviewData?.queryParam?.split('-')[8] || repriceData?.query_data?.split('-')[8];

      //bookData.w[returnDetail.w]
      const flightDetails = {
        header: {
          src: srcText,
          dest: desText,
          refundability
        },
        footer: {
          travelClass
        },
        flights: returnFlightDetail.f
      };

      return flightDetails;
    } else {
      return { isLoading: true };
    }
  }
);

export const carrierNameSelector = createSelector(
  [bookingReviewDataSelector, repriceDataSelector, minifiedDataSelector],
  (bookingReviewData, repriceData, minifiedData) => {
    const bookData =
      (bookingReviewData && bookingReviewData.bookData) ||
      (repriceData && repriceData.minified_data) ||
      minifiedData;

    if (bookData) {
      try {
        const flightEntry = bookData.o[0].f[0];
        const carriers = bookData && bookData.c;
        const carrierName = carriers[flightEntry.c].n;
        return carrierName;
      } catch {
        return null;
      }
    } else {
      return null;
    }
  }
);

export const carrierCodesSelector = createSelector(
  [bookingReviewDataSelector, repriceDataSelector, minifiedDataSelector],
  (bookingReviewData, repriceData, minifiedData) => {
    const bookData =
      (bookingReviewData && bookingReviewData.bookData) ||
      (repriceData && repriceData.minified_data) ||
      minifiedData;

    if (bookData) {
      try {
        const onwardFlights = bookData.o[0].f;
        const carriers = bookData && bookData.c;
        const carrierCode = [];
        for (let i = 0; i < onwardFlights.length; i++) {
          const flightEntry = onwardFlights[i];
          carrierCode.push(carriers[flightEntry.c].c);
        }
        return carrierCode;
      } catch {
        return [];
      }
    } else {
      return [];
    }
  }
);

export const flightDetailsCardSelector = createSelector(
  [bookingReviewDataSelector, repriceDataSelector, minifiedDataSelector],
  (bookingReviewData, repriceData, minifiedData): IFlightDetailsCardSelector => {
    const bookData =
      (bookingReviewData && bookingReviewData.bookData) ||
      (repriceData && repriceData.minified_data) ||
      minifiedData;

    if (!bookData) {
      return { isLoading: true };
    }
    const airports = bookData && bookData.a;
    const carriers = bookData && bookData.c;
    const date = bookData && bookData.d;
    const terminal = bookData && bookData.t;
    const aircrafts = (bookData && bookData.e) || [];

    bookData.o = bookData.o instanceof Array ? bookData.o : [bookData.o];

    const flightCardDetails = {
      airports,
      carriers,
      date,
      terminal,
      aircrafts
    };

    return flightCardDetails;
  }
);

export const offersSelector = createSelector(
  [repriceDataSelector, loadingPromoCodeSelector],
  (repriceData, loadingPromoCode) => {
    const offers = repriceData && repriceData.o;
    const offersArray = offers?.filter(offer => offer?.ch !== 'gocash_popup') || [];
    const appliedOffer = offersArray && offersArray.find(offer => offer.ap && offer.en);
    const offerModal = repriceData && repriceData.od;
    const containsOffer =
      repriceData && repriceData.o && Array.isArray(repriceData.o) && repriceData.o.length > 0;
    const isLoggedIn = repriceData && repriceData.emp && Object.keys(repriceData.emp).length > 0;
    const goCashApplied = repriceData && repriceData.ga;
    const goCashAmt = repriceData && repriceData.gc;
    const offerCount = typeof repriceData?.oc?.uo === 'undefined' ? 2 : repriceData?.oc?.uo;
    const showOfferStrip =
      typeof repriceData?.oc?.cap === 'undefined' ? true : repriceData?.oc?.cap;
    return {
      containsOffer,
      offers: offersArray,
      appliedOffer,
      offerModal,
      loadingPromoCode,
      isLoggedIn,
      goCashApplied,
      goCashAmt,
      offerCount,
      showOfferStrip
    };
  }
);

export const goCashSelector = createSelector([repriceDataSelector], repriceData => {
  const offers = repriceData && repriceData.o;
  return offers && offers.find(offer => offer?.ch === 'gocash_popup');
});

export const goTribeDataSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.tb
);

export const bookingHelpSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.iivr || ''
);

export const preFareBreakupSelector = createSelector(
  [repriceDataSelector, bookingReviewDataSelector],
  (repriceData, bookingReviewData) => {
    const { bookData } = bookingReviewData;
    const onwFlight = bookData?.o[0];
    const retFlight = bookData?.r[0];
    const onwardPrice = onwFlight?.p || 0;
    const returnPrice = retFlight?.p || 0;
    const onwardPriceBase = bookData?.o[0]?.b || 0;
    const returnPriceBase = bookData?.r[0]?.b || 0;
    const onwardPriceTaxes = bookData?.o[0]?.x || 0;
    const returnPriceTaxes = bookData?.r[0]?.x || 0;
    const discounts = bookData?.g || null;
    let disAmt = 0;
    if (onwFlight && retFlight && discounts) {
      const key = `o${onwFlight.i}r${retFlight.i}`;
      disAmt = discounts[key] || 0;
    }
    const showConvFee = repriceData && repriceData.cf && repriceData.cff;
    const sf = [];
    if (onwardPriceBase || returnPriceBase) {
      sf.push({
        k: 'Base Fare',
        v: onwardPriceBase + returnPriceBase - disAmt
      });
    }
    if (onwardPriceTaxes || returnPriceTaxes) {
      sf.push({
        k: 'Taxes and Fees',
        v: onwardPriceTaxes + returnPriceTaxes
      });
    }
    let totalAmt = (repriceData && repriceData.nr) || onwardPrice + returnPrice - disAmt;

    if (showConvFee) {
      totalAmt = totalAmt + parseFloat(repriceData.cf);
    }

    const fareBreakup = [];
    if (onwardPrice + returnPrice) {
      fareBreakup.push({
        k: 'Fare',
        v: onwardPrice + returnPrice - disAmt,
        sf,
        sub: 'Base fare and taxes'
      });
    }

    let addonFares = [];
    //let fareLockBreakup = [];

    return {
      totalAmt,
      discountAmt:
        (repriceData && repriceData.p && repriceData.p.a && Math.abs(repriceData.p.a)) || 0,
      fareBreakup,
      addonFares,
      goCashAmt: (repriceData && repriceData.ga && repriceData.gc) || 0,
      addonsAmount: (repriceData && repriceData.aa) || 0
      //fareLockBreakup
    };
  }
);

export const fareBreakupSelector = createSelector(
  [repriceDataSelector, getPriceDataSelector],
  (repriceData, getPriceData) => {
    const showConvFee = repriceData && repriceData.cf && repriceData.cff;
    let goCashAmt = (repriceData && repriceData.ga && repriceData.gc) || 0;
    let priceBreakup =
      repriceData && repriceData.fs
        ? JSON.parse(JSON.stringify(repriceData.fs))
        : getPriceData && getPriceData.fs
        ? JSON.parse(JSON.stringify(getPriceData.fs))
        : null;

    let addonFares = [];
    //let fareLockBreakup = [];

    if (priceBreakup?.fbs && getPriceData && repriceData) {
      const { airline_fare, fare_diff_absorbed, farediff } = getPriceData;
      const { frl } = repriceData;
      const priceLockInd = priceBreakup?.fbs?.findIndex(fb => fb.key === 'FARE_LOCK');
      if (priceLockInd >= 0) {
        priceBreakup.fbs[priceLockInd]?.fares?.forEach(fare => {
          if (fare_diff_absorbed) {
            fare.ftt = {
              fda: fare_diff_absorbed,
              la: frl, //locked at
              ft: airline_fare, //fare today
              isEqOrAboveThreshold: farediff > 0
            };
            //fareLockBreakup.push(fare);
          }
        });
      }
    }

    if (repriceData.reviewStreaming) {
      const appliedGoCash = repriceData?.o?.find(itm => itm.k === 'gocash' && itm.ap);
      if (appliedGoCash) {
        goCashAmt = appliedGoCash.amt;
      }
    }

    return {
      totalAmt:
        repriceData && (showConvFee ? repriceData.nr + parseFloat(repriceData.cf) : repriceData.nr),
      discountAmt:
        (repriceData && repriceData.p && repriceData.p.a && Math.abs(repriceData.p.a)) || 0,
      priceBreakup,
      addonFares: addonFares,
      goCashAmt,
      addonsAmount: repriceData && repriceData.aa
      //fareLockBreakup
    };
  }
);

export const ruleDataSelector = createSelector(
  [baggageDataSelector, miniRulesDataSelector, metaDataSelector],
  (baggageData, miniRulesData, metaData) => {
    return {
      baggageData,
      miniRulesData,
      metaData
    };
  }
);

export const flightBaggageInfoSelector = createSelector([baggageDataSelector], baggageData => {
  let baggageInfo = {};
  if (baggageData) {
    for (const key in baggageData) {
      if (baggageData[key]) {
        const data = baggageData[key];
        const flight = `${data.carrierid}${data.flightno}`;
        baggageInfo[flight] = {
          cb: {
            name: data.terms[0].cb.name,
            value: data.terms[0].cb.value
          },
          hb: {
            name: data.terms[0].hb.name,
            value:
              data.terms[0].hb.kg_per_pc && data.terms[0].hb.pc
                ? `${data.terms[0].hb.kg_per_pc} Kgs, ${data.terms[0].hb.pc} piece`
                : data.terms[0].hb.value
          }
        };
      }
    }
  }
  return baggageInfo;
});

export const onwardTravelDetailsSelector = createSelector(
  [onwardFlightDetailsSelector, flightDetailsCardSelector, flightBaggageInfoSelector],
  (onwardFlightDetails, flightDetailsCard, flightBaggageInfo) => {
    if (onwardFlightDetails.isLoading || flightDetailsCard.isLoading) {
      return { isLoading: true };
    }
    const { airports, carriers, date, terminal, aircrafts } = flightDetailsCard;

    const flights =
      onwardFlightDetails &&
      onwardFlightDetails.flights &&
      onwardFlightDetails.flights.map(onwardFlightDetail => {
        return parseTravelDetails({
          flightItem: onwardFlightDetail,
          carriers,
          aircrafts,
          date,
          airports,
          terminal,
          baggageInfo: flightBaggageInfo
        });
      });
    return { ...onwardFlightDetails, flights };
  }
);

export const returnTravelDetailsSelector = createSelector(
  [returnFlightDetailsSelector, flightDetailsCardSelector, flightBaggageInfoSelector],
  (returnFlightDetails, flightDetailsCard, flightBaggageInfo) => {
    if (returnFlightDetails === null) {
      return null;
    }
    if (returnFlightDetails.isLoading || flightDetailsCard.isLoading) {
      return { isLoading: true };
    }
    const { airports, carriers, date, terminal, aircrafts } = flightDetailsCard;

    const flights =
      returnFlightDetails &&
      returnFlightDetails.flights &&
      returnFlightDetails.flights.map(returnFlightDetail => {
        return parseTravelDetails({
          flightItem: returnFlightDetail,
          carriers,
          aircrafts,
          date,
          airports,
          terminal,
          baggageInfo: flightBaggageInfo
        });
      });
    return { ...returnFlightDetails, flights };
  }
);

export const fRulesContactDetailsSelector = createSelector(repriceDataSelector, repriceData => {
  const rowFields = repriceData?.f_rules?.contactDetails?.rowFields;
  if (rowFields) {
    return {
      mobilecode: rowFields.COUNTRY_CODE,
      email: rowFields.EMAIL || '',
      mobile: rowFields.MOBILE_NUMBER || ''
    };
  }

  return null;
});

export const travellersDetailsSelector = createSelector(
  [
    repriceDataSelector,
    savedTravellersSelector,
    newTravellersSelector,
    paxTypeCountSelector,
    totalPaxSelector,
    dateOfTravelSelector,
    gstEnabledSelector,
    contactDetailsSelector,
    travellerFormRulesSelector,
    isDomesticSelector,
    fRulesContactDetailsSelector,
    savedTravellersNewSelector,
    paxTypeCountSelectorNew
  ],
  (
    repriceData,
    savedTravellers,
    newTravellers,
    pax,
    totalPax,
    date,
    GSTEnbaled,
    contactDetails,
    travellerFormRules,
    isDomestic,
    fRulesContactDetails,
    savedTravellersNew,
    paxNew
  ) => {
    return {
      isLoggedIn: repriceData && repriceData.emp && Object.keys(repriceData.emp).length > 0,
      savedTravellers,
      newTravellers,
      pax,
      totalPax,
      date,
      GSTEnbaled,
      contactDetails,
      travellerFormRules,
      isDomestic,
      fRulesContactDetails,
      savedTravellersNew,
      paxNew
    };
  }
);

export const isNewTravellerFlowSelector = createSelector([repriceDataSelector], repriceData => {
  return !!repriceData?.f_rules?.newTravellerFlow;
});

export const availablePaxTypeList = createSelector(
  [selectedTravellersSelector, paxTypeCountSelector],
  (selectedTravellers, paxTypeCount) => {
    const paxTypeList = [];
    Object.keys(paxTypeCount).forEach(key => {
      if (selectedTravellers[key].length < paxTypeCount[key]) {
        paxTypeList.push(key);
      }
    });
    return paxTypeList;
  }
);

export const availablePaxTypeListNew = createSelector(
  [selectedTravellersSelector, paxTypeCountSelectorNew],
  (selectedTravellers, paxTypeCount) => {
    const paxTypeList = [];
    Object.keys(paxTypeCount).forEach(key => {
      if (selectedTravellers[key?.toLowerCase()].length < paxTypeCount[key]) {
        paxTypeList.push(key);
      }
    });
    return paxTypeList;
  }
);

export const addTravellersSelector = createSelector(
  [
    newTravellersSelector,
    currentTravellersSelector,
    travellerFormRulesSelector,
    availablePaxTypeList,
    selectedTravellersSelector,
    dateOfTravelSelector,
    isDomesticSelector,
    savedTravellersSelector,
    nationalityListSelector,
    passportCountyListSelector,
    paxTypeCountSelector,
    availablePaxTypeListNew,
    savedTravellersNewSelector,
    paxTypeCountSelectorNew
  ],
  (
    newTravellers,
    currentTravellers,
    paxWiseTravellerFormDetails,
    paxTypeList,
    selectedTravellers,
    dateOfTravel,
    isDomestic,
    savedTravellers,
    nationalityList,
    passportCountyList,
    pax,
    paxTypeListNew,
    savedTravellersNew,
    paxNew
  ) => ({
    newTravellers,
    currentTravellers,
    paxWiseTravellerFormDetails,
    paxTypeList,
    selectedTravellers,
    dateOfTravel,
    isDomestic,
    savedTravellers,
    nationalityList,
    passportCountyList,
    pax,
    paxTypeListNew,
    savedTravellersNew,
    paxNew
  })
);

export const contactFormDetailsSelector = createSelector(
  repriceDataSelector,
  repriceData =>
    repriceData &&
    repriceData.f_rules &&
    repriceData.f_rules.travellerFormDetail &&
    repriceData.f_rules.travellerFormDetail.contactFormDetails
);

export const addGstPageSelector = createSelector(
  [gstFormDetailsSelector, gstDetailsSelector, gstEnabledSelector],
  (gstFormDetails, GSTDetails, gstEnabled) => ({
    gstFormDetails,
    GSTDetails,
    gstEnabled
  })
);

export const addContactDetailsPageSelector = createSelector(
  [contactFormDetailsSelector, fRulesContactDetailsSelector, contactDetailsSelector],
  (contactFormDetails, fRulesContactDetails, details) => ({
    contactFormDetails,
    fRulesContactDetails,
    details
  })
);

export const mbSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.mb
);

export const seatDataSelector = createSelector(brpStateSelector, brp => brp.seatData);

export const seatInfoSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.seat_info
);

export const seatBannerSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.seat_banner
);

export const mealBannerSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData && repriceData.meal_banner
);

export const ancillaryPriorityOrderSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.anc_prior
);

export const addedBenefitsSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.ab
);

export const exclusiveGiftsSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.clm_details
);

export const addonsAmountSelector = createSelector(
  [mealsAmtSelector, baggageAmtSelector, seatsAmtSelector],
  (mealsAmt, baggageAmt, seatsAmt) => ({
    mealsAmt,
    baggageAmt,
    seatsAmt
  })
);

export const covidInsuranceAmtSelector = createSelector(addedBenefitsSelector, addedBenefits => {
  let amt = 0;
  if (addedBenefits) {
    for (let i = 0; i < addedBenefits.length; i++) {
      const addon = addedBenefits[i];
      if (addon.k === 'Covid Insurance') {
        return addon.v;
      }
    }
  }
  return amt;
});

export const availableAddonsSelector = createSelector(repriceDataSelector, repriceData => {
  let isMealsAvailable = false;
  let isBaggageAvailable = false;
  const isSeatAvailable = repriceData.se && repriceData.sl;
  const addons = [];

  if (repriceData.mb && Array.isArray(repriceData.mb)) {
    repriceData.mb.forEach(data => {
      if (data.m && Array.isArray(data.m) && data.m.length > 0) {
        isMealsAvailable = true;
      }
      if (data.b && Array.isArray(data.b) && data.b.length > 0) {
        isBaggageAvailable = true;
      }
    });
  }

  if (isSeatAvailable) {
    addons.push('Seats');
  }
  if (isMealsAvailable) {
    addons.push('Meals');
  }
  if (isBaggageAvailable) {
    addons.push('Baggage');
  }

  return addons;
});

export const flightSearchDataSelector = createSelector(
  [queryArraySelector, isDomesticSelector],
  (queryArray, isDomestic) => {
    return {
      source: { iata: queryArray[1] }, // fix split of undefined
      destination: { iata: queryArray[2] },
      departure: { yyyymmdd: queryArray[3] },
      arrival: { yyyymmdd: queryArray[4] },
      passengers: {
        adult: parseInt(queryArray[5]),
        child: parseInt(queryArray[6]),
        infant: parseInt(queryArray[7])
      },
      flightClass: { value: queryArray[8] },
      journeyType: !queryArray[4] ? 'ow' : 'rt',
      travelType: isDomestic ? 'domestic' : 'international'
    };
  }
);

export const seatsLayoutSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.sl
);

export const seatSelectionTypeSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.sst || ''
);

export const seat_infoSelector = createSelector(
  [brpStateSelector, repriceDataSelector],
  (brp, repriceData) => brp.seatInfo || repriceData.seat_info
);

export const seatSelectionChangedSelector = createSelector(
  [brpStateSelector, repriceDataSelector],
  (brp, repriceData) => !!brp.seatInfo
);

export const goTribeDiscountSelector = createSelector(repriceDataSelector, data =>
  data && data.tba && data.tb && data.tb.b && (data.tb.b.s || data.tb.b.sr) ? data.tb.b : undefined
);

export const hasBundledAddonsSelector = createSelector(repriceDataSelector, data =>
  data.bd && data.bd.an && (data.bd.an.seat || data.bd.an.meal || data.bd.an.baggage) ? true : false
);

export const activeSectionSelector = createSelector(brpStateSelector, brp => brp.activeSection);

const getTotalCount = obj => {
  let count = 0;
  if (obj) {
    Object.keys(obj).map(key => {
      count += obj[key].length;
    });
  }
  return count;
};

export const selectedSeatCountSelector = createSelector(brpStateSelector, brp => {
  return getTotalCount(brp.selectedSeats);
});

export const selectedMealCountSelector = createSelector(brpStateSelector, brp => {
  return getTotalCount(brp.selectedMeals);
});

export const selectedBaggageCountSelector = createSelector(brpStateSelector, brp => {
  return getTotalCount(brp.selectedBaggage);
});

export const covidInsuranceAddedSelector = createSelector(addedBenefitsSelector, addedBenefits => {
  if (addedBenefits) {
    return (
      addedBenefits.filter(addon => {
        return addon.k === 'Covid Insurance';
      }).length === 1
    );
  }
  return false;
});

export const covidInsurancePriceBreakupSelector = createSelector(
  repriceDataSelector,
  repriceData => {
    const fbs = repriceData?.fs?.fbs;
    const addonsInd = fbs?.findIndex(addon => addon.key === 'Add_Ons');
    const fareAddons = addonsInd >= 0 && repriceData?.fs?.fbs[addonsInd];
    return (
      fareAddons?.fares?.findIndex(fare => fare?.key == 'covid_insurance' && fare?.price > 0) >= 0
    );
  }
);

export const hasPaymentOptionSelector = createSelector(repriceDataSelector, repriceData => {
  // return repriceData && (repriceData.r || repriceData.pp || (repriceData.ed && repriceData.ed.e)) && !isReschedule
  return (
    (repriceData && (repriceData.r || repriceData.pp || (repriceData.ed && repriceData.ed.e))) ||
    false
  );
});

export const selectedPaymentOptionSelector = createSelector(repriceDataSelector, repriceData => {
  if (repriceData && repriceData.ft) {
    return repriceData.ft;
  }
  return 'full';
});

export const reserveNowDataSelector = createSelector(repriceDataSelector, repriceData => {
  if (repriceData && repriceData.r && Object.keys(repriceData.r).length > 0) {
    return repriceData.r;
  }
  return null;
});

export const partialPayDataSelector = createSelector(repriceDataSelector, repriceData => {
  if (repriceData && repriceData.pp && Object.keys(repriceData.pp).length > 0) {
    return repriceData.pp;
  }
  return null;
});

export const emiDataSelector = createSelector(repriceDataSelector, repriceData => {
  if (repriceData && repriceData.ed && repriceData.ed.e) {
    return repriceData.ed;
  }
  return null;
});

// export const priceLockAmountSelector = createSelector(
//   repriceDataSelector,
//   repriceData => repriceData.frla || 0
// );

// export const priceLockedAtAmountSelector = createSelector(
//   repriceDataSelector,
//   repriceData => repriceData.frl || 0
// );

// export const lockPriceSoldoutSelector = createSelector(
//   brpStateSelector,
//   brp => brp.priceLockSoldout
// );

export const zeroCancellationDataSelector = createSelector([repriceDataSelector], repriceData => {
  // if (repriceData && repriceData.zc) {
  //   return repriceData.zcd;
  // }
  // return null;
  if (repriceData && repriceData.zero_cancellation) {
    return repriceData.zero_cancellation;
  }
  return null;
});

export const freeDateChangeDataSelector = createSelector([repriceDataSelector], repriceData => {
  if (repriceData && repriceData.fdc) {
    return repriceData.fdc;
  }
  return null;
});

export const cancellationAndFreeDateChangeOrderSelector = createSelector(
  [repriceDataSelector],
  repriceData => repriceData?.cpfdc || {}
);

export const zeroCancellationAddedSelector = createSelector([repriceDataSelector], repriceData => {
  // if (repriceData && repriceData.zcd && repriceData.zcd.ap) {
  //   return repriceData.zcd.ap;
  // }
  if (repriceData && repriceData.zero_cancellation && repriceData.zero_cancellation.ap) {
    return repriceData.zero_cancellation.ap;
  }
  return false;
});

export const flightSoldOutDetailsSelector = createSelector(
  getPriceDataSelector,
  getPriceData => getPriceData?.ret_frlck_error || null
);

export const goluxeDataSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.giff || {}
);

export const xSellSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.xsell_hotel || null
);

// Lite

export const brpDataSelector = createSelector(brpStateSelector, brp => brp.brpData);

export const fareFamilyUpdatePopupInfoSelector = createSelector(
  brpDataSelector,
  brpData => brpData?.fare_options?.static
);

export const fareFamilyUpdateInfoSelector = createSelector(
  brpDataSelector,
  brpData => brpData?.fare_options
);

export const searchIdSelector = createSelector(
  brpDataSelector,
  brpData => brpData?.query?.['search-id'] || ''
);

export const searchQuerySelector = createSelector(
  brpDataSelector,
  brpData => brpData?.query?.query
);

export const srpGroundupSelector = state => state.srpLite;

export const thorDataSelector = createSelector(srpGroundupSelector, srp => srp.thorData || {});

export const onwardSelectedFlightSelector = createSelector(
  srpGroundupSelector,
  srp => srp.onwardSelectedFlight
);

export const returnSelectedFlightSelector = createSelector(
  srpGroundupSelector,
  srp => srp.returnSelectedFlight
);

export const queryParamSelector = createSelector(
  srpGroundupSelector,
  srpGroundup => srpGroundup.queryParam
);

export const thorGroundDataSelector = createSelector(
  srpGroundupSelector,
  srp => srp.thorData || {}
);

export const queryBRPSelector = createSelector(thorGroundDataSelector, data => data.query);

export const searchStringSelector = createSelector(srpGroundupSelector, srp => srp.searchString);

export const repriceRequestGroundupSelector = createSelector(
  [
    onwardSelectedFlightSelector,
    returnSelectedFlightSelector,
    flavourSelector,
    queryBRPSelector,
    searchStringSelector,
    selectedPromoCodeSelector
  ],
  (onwardSelectedFlight, returnSelectedFlight, flavour, queryBrp, searchString, promoCode) => {
    const { rid, rcoms } = getRepriceReqDetails(
      queryBrp,
      onwardSelectedFlight,
      returnSelectedFlight
    );
    return {
      // ...urlSearchParam,
      flavour,
      v: 1.6,
      rid,
      rcoms: JSON.stringify(rcoms),
      ...(promoCode && { applied_addons: JSON.stringify(promoCode) })
    };
  }
);

export const searchDetailsSelector = createSelector(
  [
    onwardSelectedFlightSelector,
    returnSelectedFlightSelector,
    flavourSelector,
    queryBRPSelector,
    searchStringSelector
  ],
  (onwardSelectedFlight, returnSelectedFlight, flavour, queryBrp, searchString) => {
    const { rid, rcoms } = getRepriceReqDetails(
      queryBrp,
      onwardSelectedFlight,
      returnSelectedFlight
    );
    return {
      //   ...urlSearchParam,
      flavour,
      v: 1.6,
      rid,
      id: rcoms
    };
  }
);

export const activePaxTypeSelector = createSelector(brpStateSelector, brp => brp.activePaxtype);
export const activePaxTitleSelector = createSelector(brpStateSelector, brp => brp.activePaxtitle);

export const importantInfoSelector = createSelector(
  brpStateSelector,
  brp => (brp.impInfo && brp.impInfo.sii) || null
);

export const brpLoadingSelector = createSelector(brpStateSelector, brp => brp.brpLoading);

export const offersModalSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData.p
);

export const isSRPOfferSelector = createSelector(selectedPromoCodeSelector, promocode => {
  return promocode;
});

export const offerSuggestionSelector = createSelector(repriceDataSelector, repriceData => {
  return repriceData.osug;
});

export const baggageTxtSelector = createSelector(brpStateSelector, brp => brp.selectedBaggageText);

export const mealsCountDataSelector = createSelector(
  brpStateSelector,
  brp => brp.selectedMealsText
);

export const seatsTextSelector = createSelector(brpStateSelector, brp => brp.selectedSeatsText);

export const onwardSeatsCountSelector = createSelector(
  brpStateSelector,
  brp => brp.onwardSeatCount
);

export const returnSeatsCountSelector = createSelector(
  brpStateSelector,
  brp => brp.returnSeatCount
);

export const isSRPOfferShowSelector = createSelector(brpStateSelector, brp => brp.srpOfferShow);

export const getPricePopUpDataSelector = createSelector(
  getPriceDataSelector,
  getPriceData => getPriceData?.bottom_sheet || null
);

export const repricePopUpDataSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.bottom_sheet || null
);

export const selectedFareFamilyIndexSelector = createSelector(
  brpStateSelector,
  brp => brp.selectedFareFamilyIndex
);

export const isFareFamilyUpdatedSelector = createSelector(
  brpStateSelector,
  brp => brp.isFareFamilyUpdated
);

export const brpOmnitureInfoSelector = createSelector(brpStateSelector, brp => {
  return brp?.brpOmnitureInfo;
});

export const commonCTASelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.common_cta
);

export const brpMissedOmnitureEventsSelector = createSelector(brpStateSelector, brp => {
  return brp?.brpMissedOmnitureEvents;
});

export const showSeatPersuasionToolTipSelector = createSelector(brpStateSelector, brp => {
  return brp.showSeatPersuasionToolTip;
});

export const specialFareWidgetDataSelector = createSelector(
  repriceDataSelector,
  repriceData => repriceData?.special_fare_widget || null
);

export const popupDataSelector = createSelector(repriceDataSelector, repriceData => {
  return repriceData.popup || null;
});

export const expLabelForAnalyticsSelector = createSelector(
  brpStateSelector,
  brp => brp.expLabelForAnalytics
);

export const searchStringFromRepriceDataSelector = createSelector(
  repriceDataSelector,
  repriceData => {
    return repriceData?.query_data || '';
  }
);

export const voidCancelationDataSelector = createSelector(
  brpStateSelector,
  brp => brp.voidCancelationData
);

export const baggageCancellationRulesTabSelector = createSelector(
  brpStateSelector,
  brp => brp.baggageCancellationRulesTab
);

export const mealsTabSelector = createSelector(brpStateSelector, brp => brp.selectedMealsTab);

// export const isReviewTravellerDetailsSelector = createSelector(
//   [travellerFormRulesSelector, isDomesticSelector],
//   (formRules, isDomestic) => {
//     return Boolean(!isDomestic && formRules?.ADULT?.rowFields?.PASSPORT_NUMBER);
//   }
// );
